import * as THREE from 'three';
import { C, SEASON_LEAF } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { makeDowntownCrowd } from '../entities/npc.js';
import { Townsperson, breadSlice, stickLegs, checkNpcClick } from '../entities/townsfolk.js';
import { createAmbient } from '../entities/ambient.js';
import { HUD } from '../ui/hud.js';
import { getCrumbs, addCrumbs, getInventory, addSeeds, spendSeeds, addCrops, spendCrops } from '../core/save.js';
import { CROPS, CROP_IDS } from '../core/crops.js';
import { questsForGiver, acceptQuest, turnIn, isReady as isQuestReady, objectiveProgress, QUESTS } from '../core/quests.js';
import { getSeason, isNight } from '../core/world.js';
import { WeatherSystem, getWeatherState } from '../core/weather.js';

// Downtown Toast Town — the built district south of the plaza.
// Three storefronts, the museum, the sheriff, the bank and Chip's Pantry line a
// paved street that ends at a gate back to the town square.
//
// Downtown's silhouette is BUILT FRONTAGE: not a lawn or a disc, but a city
// block. A continuous wall of buildings and alley fences stands exactly on the
// walk line so every gap is a real exit.

export function createDowntownScene(){
  return () => new DowntownScene();
}

const GROUND_R = 34;      // how far the pavement is drawn
const WALK_R   = 26;      // how far you may walk — the perimeter wall stands here
const ROAD_W   = 9;       // width of the main street

const LAMP_ON  = 0xFFE9A8;
const LAMP_OFF = 0xD8D2C2;

const STOREFRONTS = [
  { x: -15, color: 0xB88FC4, awning: 0x8E5EA0, name: 'Toast Threads',     scene: 'clothing',   tag: '👕' },
  { x:   0, color: 0x9CBE93, awning: 0x6E9C63, name: 'Crumb & Comfort',   scene: 'furniture',  tag: '🛋' },
  { x:  15, color: 0xD98C6A, awning: 0xB5643F, name: 'The Toasted Table', scene: 'restaurant', tag: '🍽' }
];

class DowntownScene {
  constructor(){
    this.panels = [];
    this.lamps = [];
    this.state = 'explore';

    this.season = getSeason();
    this.leaf = SEASON_LEAF[this.season] || C.sprout;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(C.sky);
    this.scene.fog = new THREE.Fog(C.sky, 40, 96);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 200);
    this.camDist = 18;
    this.camTarget = new THREE.Vector3(0, 1.6, 1);
    this.azimuth = 0; this.polar = 0.52;

    this._buildLights();
    this._buildGround();
    this._buildPerimeter();
    STOREFRONTS.forEach(s => this._storefront(s));
    this._buildConvenienceStore();
    this._buildBank();
    this._buildSheriffStation();
    this._buildClockTower();
    this._buildMuseum();
    this._buildGate();
    this._buildStreetFurniture();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 10);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    // roster-driven street crowd — spread across the larger block
    this.folk = makeDowntownCrowd(9);
    this.folk.forEach((f, i) => {
      const a = (i / this.folk.length) * Math.PI * 2 + 0.6;
      const r = 6 + (i % 3) * 5.5;
      const p = new THREE.Vector3(Math.sin(a) * r, 0, Math.cos(a) * r);
      f.root.position.copy(p);
      f.home.copy(p);
      this.scene.add(f.root);
    });

    this.ambient = createAmbient({ center: new THREE.Vector3(0, 0, 2), radius: 22, butterflies: 3, birds: 2, fireflies: 5 });
    this.scene.add(this.ambient.group);

    this.weather = new WeatherSystem(this.scene, { center: new THREE.Vector3(0, 0, 2), radius: WALK_R });
    this._applyWeather();

    this.clerk = this._makeClerk();
    this.scene.add(this.clerk.root);

    this.hotspots = STOREFRONTS.map(s => ({
      pos: new THREE.Vector3(s.x, 0, -5.4), r: 2.6,
      label: `enter ${s.name}`, action: () => this.manager.goTo(s.scene, { from: 'downtown' })
    }));
    this.hotspots.push({ pos: new THREE.Vector3(0, 0, 24), r: 3.2, label: '← back to the town square', action: () => this.manager.goTo('town', { from: 'downtown' }) });
    this.hotspots.push({ pos: new THREE.Vector3(-10, 0, 8.2), r: 2.4, label: 'enter Sheriff Station', action: () => this.manager.goTo('sheriff', { from: 'downtown' }) });
    this.hotspots.push({ pos: new THREE.Vector3(10, 0, 8.2), r: 2.4, label: 'enter The Bread Bank', action: () => this.manager.goTo('bank', { from: 'downtown' }) });
    this.hotspots.push({ pos: new THREE.Vector3(-22, 0, -5.4), r: 2.6, label: 'enter Toast Town Museum', action: () => this.manager.goTo('museum', { from: 'downtown' }) });
    this.hotspots.push({ pos: new THREE.Vector3(-20, 0, 8.2), r: 2.4, label: 'enter Chip\'s Pantry', action: () => this._talkToClerk() });
    this.nearSpot = null;
    this.clerkPanel = null;
    this._updateLamps();
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.72));
    const shift = { spring: { fog: C.sky }, summer: { fog: C.sky }, autumn: { fog: 0xE6D6A8 }, winter: { fog: 0xDAE6E8 } }[this.season];
    if (shift){ this.scene.fog.color.setHex(shift.fog); this.scene.background.setHex(shift.fog); }
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.9);
    sun.position.set(8, 18, 12); sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 38;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
  }

  _buildGround(){
    // one paved ground material for the whole district
    const pavement = new THREE.Mesh(new THREE.CircleGeometry(GROUND_R, 64), new THREE.MeshLambertMaterial({ color: C.stoneDim }));
    pavement.rotation.x = -Math.PI/2; pavement.receiveShadow = true; this.scene.add(pavement);

    // one path material: the main avenue and the crossing in front of the shops
    const avenue = new THREE.Mesh(new THREE.PlaneGeometry(ROAD_W, 54), new THREE.MeshLambertMaterial({ color: C.path }));
    avenue.rotation.x = -Math.PI/2; avenue.position.set(0, 0.01, 1); avenue.receiveShadow = true; this.scene.add(avenue);

    const crossing = new THREE.Mesh(new THREE.PlaneGeometry(44, 10), new THREE.MeshLambertMaterial({ color: C.path }));
    crossing.rotation.x = -Math.PI/2; crossing.position.set(0, 0.012, -8); crossing.receiveShadow = true; this.scene.add(crossing);

    // road centre line dashes
    for (let z = -18; z <= 22; z += 2.8)
      box(0.14, 0.02, 1.2, C.cream, 0, 0.03, z, this.scene);
  }

  // A continuous built edge on the walk limit. The only gap is the town gate to
  // the south, so an opening you can see is an opening you can use.
  _buildPerimeter(){
    const COUNT = 120;
    const GAP = 0.18; // south gate gap
    for (let i = 0; i < COUNT; i++){
      const a = (i / COUNT) * Math.PI * 2;
      const southGate = Math.abs(Math.sin(a)) < GAP && Math.cos(a) > 0;
      if (southGate) continue;

      const x = Math.sin(a) * WALK_R;
      const z = Math.cos(a) * WALK_R;

      // skip segments that would sit inside building footprints
      if (this._buildingAt(x, z)) continue;

      const g = new THREE.Group();
      g.position.set(x, 0, z);
      g.rotation.y = a;
      this.scene.add(g);

      // alley wall: tall enough that the camera cannot see over it to the
      // paved ring outside the walk line. Same palette as the ground so it
      // reads as the backs of buildings, not a fence in a field.
      box(1.9, 2.8, 0.42, C.stoneDim, 0, 1.4, 0, g).castShadow = true;
      // stone coping and a band course to break up the mass
      box(1.95, 0.16, 0.46, C.stone, 0, 2.75, 0, g);
      box(1.95, 0.16, 0.46, C.stone, 0, 1.75, 0, g);
      for (const bx of [-0.7, 0, 0.7]) cyl(0.05, 0.05, 0.65, C.crustDark, bx, 3.25, 0, g, 6);
      box(1.9, 0.12, 0.12, C.crustDark, 0, 3.55, 0, g);
      box(1.9, 0.12, 0.12, C.crustDark, 0, 2.95, 0, g);
    }

    // lamps along the sidewalks, on the diagonals
    for (let i = 0; i < 8; i++){
      const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
      const r = WALK_R - 2.2;
      this._lamp(new THREE.Vector3(Math.sin(a) * r, 0, Math.cos(a) * r));
    }
  }

  // Coarse footprint test so the perimeter fence doesn't clip through buildings.
  _buildingAt(x, z){
    const rects = [
      { x: -15, z: -10, w: 6, d: 5 }, { x: 0, z: -10, w: 6, d: 5 }, { x: 15, z: -10, w: 6, d: 5 },
      { x: -22, z: -10, w: 5.5, d: 5 }, { x: 20, z: -4, w: 4, d: 4 },
      { x: -10, z: 10, w: 4.5, d: 4.5 }, { x: 10, z: 10, w: 4.5, d: 4.5 }, { x: -20, z: 10, w: 5, d: 4.5 }
    ];
    for (const r of rects){
      if (Math.abs(x - r.x) < r.w / 2 + 0.8 && Math.abs(z - r.z) < r.d / 2 + 0.8) return true;
    }
    return false;
  }

  _storefront(s){
    const g = new THREE.Group(); g.position.set(s.x, 0, -10); this.scene.add(g);
    box(6.2, 4.2, 4.6, s.color, 0, 2.1, 0, g);                 // body
    const roof = box(6.6, 0.55, 5.0, C.crustDark, 0, 4.4, 0, g); roof.castShadow = true;
    box(4.8, 0.45, 1.1, s.awning, 0, 2.9, 2.5, g);             // awning
    box(2.0, 2.6, 0.35, C.crustDark, 0, 1.3, 2.2, g);          // door
    box(1.4, 1.3, 0.18, C.breeze, -2.0, 2.15, 2.2, g);         // windows
    box(1.4, 1.3, 0.18, C.breeze,  2.0, 2.15, 2.2, g);
    const sign = textSprite(`${s.tag} ${s.name}`, 30, '#FFF4DF', 5.0, 0.95);
    sign.position.set(s.x, 4.2, -7.0); this.scene.add(sign);
  }

  _buildClockTower(){
    const g = new THREE.Group(); g.position.set(20, 0, -4); this.scene.add(g);
    // base
    box(2.8, 6.5, 2.8, 0x8A7A6A, 0, 3.25, 0, g).castShadow = true;
    box(3.0, 0.5, 3.0, 0x6E5E4E, 0, 0.25, 0, g);
    // clock face
    const face = cyl(1.1, 1.1, 0.15, 0xFFF4DF, 0, 5.4, 1.45, g, 24);
    face.rotation.x = Math.PI / 2;
    const hands = box(0.08, 0.9, 0.05, 0x2E2018, 0, 5.4, 1.54, g);
    hands.rotation.x = Math.PI / 2; hands.rotation.z = -0.3;
    // roof
    const roof = cyl(0, 1.7, 1.7, 0x5A4A3E, 0, 7.4, 0, g, 4);
    roof.rotation.y = Math.PI / 4;
    // bell
    sphere(0.34, 0xE8C24B, 0, 8.2, 0, g, 12);
    const sign = textSprite('Clock Tower', 24, '#6B4A2F', 3.0, 0.8);
    sign.position.set(17.0, 6.5, -4); this.scene.add(sign);
    this.clockFace = face;
  }

  _buildMuseum(){
    const g = new THREE.Group(); g.position.set(-22, 0, -10); this.scene.add(g);
    // classical museum facade
    box(5.0, 4.2, 4.2, 0xD6CFC7, 0, 2.1, 0, g).castShadow = true;          // stone body
    box(5.3, .5, 4.5, 0x8A7A6A, 0, 4.35, 0, g);            // roof trim
    // columns
    for (const x of [-1.6, -0.6, 0.6, 1.6]){
      cyl(.16, .16, 3.2, 0xF3ECE4, x, 1.6, 2.15, g, 12);
    }
    box(4.8, .3, .18, 0x8A7A6A, 0, 3.25, 2.15, g);       // entablature
    box(1.4, 2.4, .25, 0x4A3E32, 0, 1.2, 2.12, g);        // door
    // steps
    box(4.0, .2, 1.0, 0xB0A89E, 0, .1, 2.55, g);
    box(3.5, .2, .75, 0xB0A89E, 0, .3, 2.7, g);
    const sign = textSprite('🏛 Toast Town Museum', 26, '#FFF4DF', 4.6, 0.95);
    sign.position.set(-19.0, 4.2, -7.3); this.scene.add(sign);
  }

  _buildBank(){
    const g = new THREE.Group(); g.position.set(10, 0, 10); this.scene.add(g);
    box(4.2, 3.8, 4.0, 0x9AA8B8, 0, 1.9, 0, g).castShadow = true;          // stone body
    box(4.4, .45, 4.2, C.crustDark, 0, 3.85, 0, g);        // roof trim
    box(1.0, 2.2, .25, 0x4A3E32, 0, 1.1, -2.0, g);          // door (faces street)
    box(3.0, .6, .25, 0xFFD700, 0, 2.7, -2.02, g);         // gold lintel
    const sign = textSprite('🏦 The Bread Bank', 24, '#FFF4DF', 3.0, 0.75);
    sign.position.set(8.5, 3.9, 8.0); this.scene.add(sign);
  }

  _buildSheriffStation(){
    const g = new THREE.Group(); g.position.set(-10, 0, 10); this.scene.add(g);
    box(4.2, 3.6, 4.0, 0x8A9A8A, 0, 1.8, 0, g).castShadow = true;          // concrete body
    box(4.4, .4, 4.2, 0x5A5A5A, 0, 3.65, 0, g);         // roof trim
    box(1.4, 2.2, .25, 0x3A3A3A, 0, 1.1, -2.0, g);        // door (faces street)
    // sheriff star, on the street-facing wall
    const star = new THREE.Group(); star.position.set(0, 2.4, -2.05); g.add(star);
    for (let i = 0; i < 5; i++){
      const a = (i / 5) * Math.PI * 2 - Math.PI/2;
      const tri = new THREE.Mesh(new THREE.ConeGeometry(.14, .35, 3), new THREE.MeshLambertMaterial({ color: 0xFFD700 }));
      tri.position.set(Math.cos(a)*.14, Math.sin(a)*.14, 0);
      tri.rotation.z = a - Math.PI/2;
      star.add(tri);
    }
    const sign = textSprite('⭐ Sheriff Station', 24, '#FFF4DF', 3.0, 0.75);
    sign.position.set(-8.5, 3.9, 8.0); this.scene.add(sign);
  }

  _buildConvenienceStore(){
    const g = new THREE.Group(); g.position.set(-20, 0, 10); this.scene.add(g);
    box(4.8, 3.6, 4.2, 0xF4C430, 0, 1.8, 0, g).castShadow = true;          // yellow body
    box(5.1, .45, 4.5, C.crustDark, 0, 3.75, 0, g);         // roof trim
    box(4.0, .4, 1.05, 0xE23B4E, 0, 2.6, -2.15, g);        // red awning (faces street)
    box(1.5, 2.3, .25, C.crustDark, 0, 1.15, -2.12, g);     // door
    box(1.2, 1.1, .15, C.breeze, -1.5, 2.0, -2.12, g);     // windows
    box(1.2, 1.1, .15, C.breeze,  1.5, 2.0, -2.12, g);
    const sign = textSprite('🥨 Chip\'s Pantry', 26, '#FFF4DF', 4.2, 0.95);
    sign.position.set(-20, 4.0, 7.8); this.scene.add(sign);
  }

  _makeClerk(){
    const build = (g) => {
      breadSlice(g, C.crust, C.toast, 0.58, 1.05);
      sphere(.065, 0x2E2018, -.15, 1.12, .28, g, 8);
      sphere(.065, 0x2E2018,  .15, 1.12, .28, g, 8);
      // apron
      box(.55, .7, .1, 0xFFF4DF, 0, .45, .32, g);
      // little name tag
      box(.12, .08, .02, 0x3D8C5A, .12, .55, .38, g);
      stickLegs(g, .46);
    };
    return new Townsperson({
      name: 'Chip', line: 'welcome to the pantry — we buy local!',
      pos: new THREE.Vector3(-20, 0, 8.6), facing: 0,
      wander: 0, speed: 0, bubbleY: 2.4, build
    });
  }

  _talkToClerk(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.clerk.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._renderClerkDialog();
  }

  _renderClerkDialog(){
    if (this.clerkPanel){ this.clerkPanel.remove(); this.clerkPanel = null; }
    const { available, active } = questsForGiver('clerk');
    const questRows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isQuestReady(id)){
        questRows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>objective complete!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        const p = objectiveProgress(id);
        const label = q.objective.kind === 'flag' ? q.objective.noun : `${p.have} / ${p.goal} ${q.objective.noun}`;
        questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${label}</span></div><span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      const rew = q.reward?.crumbs ? ` · reward 🍞 ${q.reward.crumbs}` : '';
      questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}${rew}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    const questBody = questRows.length ? questRows.join('') : '<p class="q-dnone">"No deliveries needed right now."</p>';

    const seeds = getInventory().seeds;
    const buyRows = CROP_IDS.map(id => {
      const c = CROPS[id];
      return `<div class="q-drow"><div><b>${c.icon} ${c.name} seeds</b><span>🍞 ${c.seedPrice} each</span></div>
        <button class="cafe-btn buy ${getCrumbs() >= c.seedPrice ? '' : 'disabled'}" data-crop="${id}">buy</button></div>`;
    }).join('');
    const crops = getInventory().crops;
    const sellRows = CROP_IDS.map(id => {
      const c = CROPS[id];
      const have = crops[id] || 0;
      return `<div class="q-drow"><div><b>${c.icon} ${c.name}</b><span>you have ${have} · sell for 🍞 ${c.sellPrice}</span></div>
        <button class="cafe-btn sell ${have > 0 ? '' : 'disabled'}" data-crop="${id}">sell</button></div>`;
    }).join('');

    const el = document.createElement('div');
    el.className = 'cafe-panel';
    el.innerHTML = `
      <div class="cafe-card q-dialog">
        <h2>Chip's Pantry</h2>
        <div class="cafe-sub">general goods & produce</div>
        <div class="q-tabs">
          <button class="cafe-btn ghost active" data-tab="buy">buy seeds</button>
          <button class="cafe-btn ghost" data-tab="sell">sell crops</button>
          <button class="cafe-btn ghost" data-tab="quests">📜 quests</button>
        </div>
        <div class="q-tab-body"><div class="q-drows" id="clerkRows">${buyRows}</div></div>
        <button class="cafe-btn ghost close">done</button>
      </div>`;
    document.body.appendChild(el);
    this.clerkPanel = el;

    const setTab = (tab) => {
      el.querySelectorAll('.q-tabs button').forEach(b => b.classList.toggle('active', b.dataset.tab === tab));
      let html = '';
      if (tab === 'buy') html = buyRows;
      else if (tab === 'sell') html = sellRows;
      else html = questBody;
      el.querySelector('#clerkRows').innerHTML = html;
      el.querySelectorAll('.buy').forEach(b => b.onclick = () => this._buyFromClerk(b.dataset.crop));
      el.querySelectorAll('.sell').forEach(b => b.onclick = () => this._sellToClerk(b.dataset.crop));
      el.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this._renderClerkDialog(); });
      el.querySelectorAll('.turnin').forEach(b => b.onclick = () => this._turnInClerk(b.dataset.id));
    };
    el.querySelectorAll('[data-tab]').forEach(b => b.onclick = () => setTab(b.dataset.tab));
    el.querySelector('.close').onclick = () => { el.remove(); this.clerkPanel = null; };
    setTab('buy');
  }

  _turnInClerk(id){
    const reward = turnIn(id);
    if (!reward) return;
    HUD.refreshCrumbs();
    this.bread.drawFace('yum');
    this._renderClerkDialog();
  }

  _buyFromClerk(id){
    const c = CROPS[id];
    if (getCrumbs() < c.seedPrice){ HUD.say(`You need ${c.seedPrice} crumbs.`); return; }
    addCrumbs(-c.seedPrice); addSeeds(id, 1);
    HUD.refreshCrumbs(); this._renderClerkDialog();
    HUD.say(`Bought ${c.icon} ${c.name} seeds.`);
  }

  _sellToClerk(id){
    const c = CROPS[id];
    if (!spendCrops(id, 1)){ HUD.say(`No ${c.name} to sell.`); return; }
    addCrumbs(c.sellPrice);
    HUD.refreshCrumbs(); this._renderClerkDialog();
    HUD.say(`Sold ${c.icon} ${c.name} for ${c.sellPrice} crumbs.`);
  }

  _buildStreetFurniture(){
    // benches facing the shop fronts
    const bench = (x, z, rot) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = rot; this.scene.add(g);
      for (const bx of [-0.85, 0.85]){
        box(0.18, 0.5, 0.55, C.stoneDim, bx, 0.25, 0, g);
      }
      box(2.1, 0.16, 0.7, C.rye, 0, 0.58, 0, g).castShadow = true;
      box(2.1, 0.55, 0.14, C.rye, 0, 0.9, -0.3, g);
    };
    bench(-8, 2.5, 0); bench(8, 2.5, 0);

    // seasonal planters along the avenue
    const planter = (x, z) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); this.scene.add(g);
      cyl(0.7, 0.58, 0.8, C.stone, 0, 0.4, 0, g, 12).castShadow = true;
      cyl(0.62, 0.62, 0.1, C.cocoa, 0, 0.82, 0, g, 12);
      sphere(0.55, this.leaf, 0, 1.1, 0, g, 12);
      for (let i = 0; i < 5; i++){
        const a = (i / 5) * Math.PI * 2;
        sphere(0.14, i % 2 ? C.raspberry : C.tangerine, Math.cos(a) * 0.38, 1.38, Math.sin(a) * 0.38, g, 8);
      }
    };
    planter(-5, 8); planter(5, 8); planter(-5, 16); planter(5, 16);

    // a few street trees, set back so they don't eat the camera on spawn
    const tree = (x, z) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); this.scene.add(g);
      cyl(0.22, 0.28, 2.0, C.rye, 0, 1.0, 0, g, 8).castShadow = true;
      const crown = sphere(1.3, this.leaf, 0, 2.7, 0, g, 12);
      crown.scale.y = 1.1; crown.castShadow = true;
    };
    tree(-12, 16); tree(12, 16); tree(-18, -2); tree(18, -2);
  }

  _lamp(pos){
    const g = new THREE.Group();
    g.position.copy(pos);
    this.scene.add(g);
    cyl(0.16, 0.22, 0.36, C.stone, 0, 0.18, 0, g, 10);        // stone base
    cyl(0.07, 0.07, 3.5, C.crustDark, 0, 1.9, 0, g, 8);
    box(0.3, 0.3, 0.3, C.crustDark, 0, 3.72, 0, g);
    const glass = sphere(0.26, LAMP_ON, 0, 3.7, 0, g, 12);
    glass.material = new THREE.MeshBasicMaterial({ color: LAMP_OFF });
    const light = new THREE.PointLight(LAMP_ON, 0, 9, 1.7);
    light.position.set(0, 3.6, 0);
    g.add(light);
    this.lamps.push({ light, glass });
  }

  _updateLamps(){
    const night = isNight();
    this.lamps.forEach(l => {
      l.light.intensity = night ? 2.4 : 0;
      l.glass.material.color.set(night ? LAMP_ON : LAMP_OFF);
    });
  }

  _buildGate(){
    cyl(0.2, 0.2, 4.2, C.rye, -3.2, 2.1, 26, this.scene, 8);
    cyl(0.2, 0.2, 4.2, C.rye,  3.2, 2.1, 26, this.scene, 8);
    box(7.0, 0.55, 0.42, C.rye, 0, 4.2, 26, this.scene);
    const sign = textSprite('↑ Toast Town square', 26, '#6B4A2F', 4.2, 1.0);
    sign.position.set(0, 4.2, 26); this.scene.add(sign);
  }

  _applyWeather(){
    const st = getWeatherState();
    this.weather.set(st.weather, st.season);
  }

  _clampToDistrict(p){
    if (p.length() > WALK_R) p.setLength(WALK_R);
    return p;
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.32, 0.72];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this.state = 'explore';
    HUD.setSubtitle('downtown');
    HUD.say('Downtown! Clothing, furniture and the restaurant line the street. Walk up to a shop, or head back up the road to the square.');
    HUD.setHint('click to walk · walk up to a storefront or the gate');
    HUD.hideHintSoon(7000);
    this._updateLamps();
  }

  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this.panels.forEach(p => p.remove()); this.panels = []; }

  _tap(ray){
    if (this.state !== 'explore') return;
    if (checkNpcClick(ray, this.folk, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = this._clampToDistrict(new THREE.Vector3(p.x, 0, p.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  update(dt){
    this.bread.update(dt);
    this._clampToDistrict(this.bread.root.position);
    this.folk.forEach(f => f.update(dt, this.bread.root.position));

    const night = isNight();
    this.ambient.update(dt, night ? 'night' : 'day');
    this.weather.update(dt, this.bread.root.position);
    this._updateLamps();

    if (this.state === 'explore'){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
      if (near && nd < near.r){
        if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
